import {
  AnimationDefinition,
  MotionStyle,
  Variants,
  motion,
} from "framer-motion";
import React, { useEffect, useRef, useState } from "react";

export type CollapsibleProps = React.PropsWithChildren & {
  open: boolean;
  style?: MotionStyle | undefined;
  contentContainerClassName?: string;
  transitionDuration?: number;
  onAnimationComplete?: (definition: AnimationDefinition) => void;
  // Allow padding extra variant props
  // e.g. display: "block" when open or display: "none" when closed to remove
  // the element from column flows that causes extra layout gaps
  additionalVariantProps?: { open?: Variants; closed?: Variants };
};

/**
 * This component animates the height of its children. If the height of
 * its children changes this component will smoothly scroll that change.
 */
export const Collapsible = ({
  open,
  style,
  contentContainerClassName,
  children,
  transitionDuration = 0.15,
  onAnimationComplete,
  additionalVariantProps,
}: CollapsibleProps) => {
  const contentContainerRef = useRef<HTMLDivElement | null>(null);
  const [height, setHeight] = useState<number | "auto">("auto");

  // This useEffect sets up a ResizeObserver to dynamically update the height
  // state based on the content's size, ensuring smooth open/close animations.
  useEffect(() => {
    if (contentContainerRef.current) {
      const resizeObserver = new ResizeObserver(entries => {
        // There is only one entry, so we can use entries[0].
        const observedHeight = entries[0].contentRect.height;
        setHeight(observedHeight);
      });

      resizeObserver.observe(contentContainerRef.current);

      return () => resizeObserver.disconnect();
    }
  }, []);

  const variants: Variants = {
    open: {
      height,
      opacity: 1,
      overflow: "visible",
      ...(additionalVariantProps?.open ?? {}),
    },
    closed: {
      height: 0,
      opacity: 0,
      overflow: "hidden",
      ...(additionalVariantProps?.closed ?? {}),
    },
  };

  return (
    <motion.div
      initial={open ? "open" : "closed"}
      animate={open ? "open" : "closed"}
      inherit={false}
      variants={variants}
      style={{ height, ...style }}
      transition={{
        ease: "linear",
        duration: transitionDuration,
      }}
      onAnimationComplete={onAnimationComplete}
    >
      <div ref={contentContainerRef} className={contentContainerClassName}>
        {children}
      </div>
    </motion.div>
  );
};
